import { useCallback, useContext, useEffect, useState } from "react";
import "../FoodMenu.css";
import { ActionMenu, Archive, ArchiveRestore, ArrowDown, ArrowUp, Button, Checkbox, Copy, Eye, EyeOff, Input, Pen, Plus, Search, Switch, Tags, Trash2, Upload } from "../../../ui";
import { ConfirmModal } from "../../../modals/Confirm";
import { menuActionsText } from "../../menuActionsText";
import { Context } from "../../../../context/Context";
import MenuSelectionModal from "../../copyModal/CopyModal";
import { archiveMenuAdditionAPI, copyAdditionAPI, createMenuAdditionAPI, fetchMenuAdditionUsagesAPI, fetchMenuAdditionsAPI, reorderMenuAdditionsAPI, restoreMenuAdditionAPI, updateMenuAdditionAPI, uploadMenuAdditionsAPI } from "../../../../utils/api/menu";
import { readMenuFile } from "../../../../utils/exportExcel";
import { localized, menuErrorMessage, useMenuRole } from "../../menuRole";
import { useAccess } from "../../../../access/hooks/useAccess";
import { useRestaurantAccess } from "../../../../access/hooks/useRestaurantAccess";

const TEXT = {
  ar: {
    listTitle: "إضافات المطعم",
    listHint: "مشتركة بين كل قوائم طعام المطعم وبنفس الأسعار.",
    formAdd: "إضافة جديدة",
    formEdit: "تعديل الإضافة",
    empty: "لا توجد إضافات بعد",
    loading: "جاري التحميل...",
    shortName: "الاسم المختصر (داخلي)",
    shortNameHint: "للتمييز الداخلي فقط ولا يظهر للعميل، مثل burger-size",
    arName: "عنوان الإضافة عربي",
    enName: "عنوان الإضافة إنجليزي",
    min: "أقل عدد",
    max: "أعلى عدد",
    mandatory: "إلزامي",
    optional: "اختياري",
    options: "الخيارات",
    optionAr: "اسم الخيار عربي",
    optionEn: "اسم الخيار إنجليزي",
    price: "السعر",
    save: "حفظ",
    cancelEdit: "إلغاء التعديل",
    archiveConfirm: "أرشفة هذه الإضافة؟",
    saved: "تم الحفظ",
    usedBy: "مستخدمة في:",
  },
  en: {
    listTitle: "Restaurant additions",
    listHint: "Shared by every menu of the restaurant, with the same prices.",
    formAdd: "New addition",
    formEdit: "Edit addition",
    empty: "No additions yet",
    loading: "Loading...",
    shortName: "Short name (internal)",
    shortNameHint: "Internal identifier, never shown to customers, e.g. burger-size",
    arName: "Arabic title",
    enName: "English title",
    min: "Min",
    max: "Max",
    mandatory: "Mandatory",
    optional: "Optional",
    options: "Options",
    optionAr: "Arabic option name",
    optionEn: "English option name",
    price: "Price",
    save: "Save",
    cancelEdit: "Cancel edit",
    archiveConfirm: "Archive this addition?",
    saved: "Saved",
    usedBy: "Used by:",
  },
};

const createEmptyForm = () => ({ shortName: "", title: { ar: "", en: "" }, min: 0, max: 1, isMandatory: false, isActive: true, options: [{ name: { ar: "", en: "" }, price: 0 }] });

// Restaurant-level additions (shared by every menu of the restaurant; linking
// to items happens in the item form). `onMutated` lets the page reload the
// menu so linked items reflect deactivated/renamed additions.
export default function MenuAdditionsModal({ menu, restaurants = [], onAdditionsChange, onMutated }) {
  const { lang } = useContext(Context);
  const t = TEXT[lang] || TEXT.ar;
  const a = menuActionsText(lang);
  const role = useMenuRole();
  const { can } = useAccess();
  const { canFor } = useRestaurantAccess(restaurants);
  const restaurantId = menu?.restaurant?._id;
  // handler-level authorization for every change made in this dialog
  const mayChange = () => can("menu.manage", { restaurantId: menu?.restaurant?.id });
  const editable = role.canWrite;

  const [additions, setAdditions] = useState([]);
  const [copyContext, setCopy] = useState(null);
  const [formData, setFormData] = useState(createEmptyForm());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [usages, setUsages] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const list = await fetchMenuAdditionsAPI(restaurantId, { includeArchived: showArchived });
      setAdditions(list);
      onAdditionsChange?.(list.filter((entry) => !entry.archivedAt));
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [restaurantId, lang, onAdditionsChange, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setFormData(createEmptyForm());
    setEditingId(null);
  };
  const updateOption = (index, patch) => setFormData((prev) => ({ ...prev, options: prev.options.map((option, i) => (i === index ? { ...option, ...patch } : option)) }));
  const moveOption = (index, to) =>
    setFormData((prev) => {
      const options = [...prev.options];
      const [moving] = options.splice(index, 1);
      options.splice(to, 0, moving);
      return { ...prev, options };
    });

  // One guarded runner for list actions: feedback + reload + menu refresh.
  const run = async (action, success = t.saved) => {
    if (busy || !mayChange()) return;
    setBusy(true);
    setNotice("");
    setError("");
    try {
      await action();
      setNotice(success);
      await load();
      onMutated?.();
    } catch (err) {
      setError(menuErrorMessage(err, lang));
      if (err.code === "ADDITION_IN_USE") setUsages({ list: err.details?.usages || [] });
    } finally {
      setBusy(false);
    }
  };
  const live = additions.filter((entry) => !entry.archivedAt);
  const reorder = (addition, step) => {
    const ids = live.map((entry) => entry._id);
    const from = ids.indexOf(addition._id);
    const [moving] = ids.splice(from, 1);
    ids.splice(from + step, 0, moving);
    return run(() => reorderMenuAdditionsAPI(restaurantId, ids));
  };
  const showUsages = (addition) =>
    run(async () => {
      setUsages({ addition, list: await fetchMenuAdditionUsagesAPI(addition._id, restaurantId) });
    }, "");

  const handleEdit = (addition) => {
    setEditingId(addition._id);
    setNotice("");
    setError("");
    setFormData({ shortName: addition.shortName || "", title: addition.title || { ar: "", en: "" }, min: addition.min ?? 0, max: addition.max ?? 1, isMandatory: Boolean(addition.isMandatory), isActive: addition.isActive !== false, options: (addition.options || []).map((o) => ({ name: o.name, price: o.price })) });
  };

  const handleSave = async () => {
    if (busy || !mayChange()) return;
    setBusy(true);
    setNotice("");
    setError("");
    const payload = { ...formData, min: Number(formData.min || 0), max: Number(formData.max || 1), options: formData.options.map((option) => ({ ...option, price: Number(option.price || 0) })) };
    try {
      if (editingId) {
        await updateMenuAdditionAPI(editingId, payload);
        setNotice(t.saved);
      } else {
        await createMenuAdditionAPI({ ...payload, restaurant: restaurantId });
        setNotice(t.saved);
      }
      resetForm();
      await load();
      onMutated?.();
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    } finally {
      setBusy(false);
    }
  };

  const handleCopySubmit = async ({ restaurantIds }) => {
    if (!mayChange()) return;
    // Source (this menu's restaurant, above) and every destination restaurant (NA-011).
    const list = Array.isArray(restaurantIds) ? restaurantIds : [];
    if (!list.length || !list.every((id) => canFor("menu.manage", id))) {
      setError(menuErrorMessage({ status: 403 }, lang));
      setCopy(null);
      return;
    }
    try {
      await copyAdditionAPI({ source: copyContext.source, targets: restaurantIds.map((id) => ({ restaurant: id })) });
      setNotice(t.saved);
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    }
    setCopy(null);
  };

  const handleUpload = async (event) => {
    if (!mayChange()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    try {
      const rows = await readMenuFile(file);
      await uploadMenuAdditionsAPI({ restaurant: restaurantId, uploadedData: rows });
      load();
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    } finally {
      event.target.value = "";
    }
  };

  return (
    <div className="food-additions-layout">
      <section className="food-additions-panel">
        <div className="food-form-section-title">
          <div>
            <h4>{t.listTitle}</h4>
            <p>
              {menu?.restaurant?.businessName} · <small>{t.listHint}</small>
            </p>
          </div>
          <div className="food-additions-panel-actions">
            {editable && (
              <label className="ui-button primary soft sm rounded icon-only food-top-action-btn food-upload-btn" title="Upload">
                <Upload size={17} />
                <input type="file" accept=".xlsx" onChange={handleUpload} aria-label="Upload additions" />
              </label>
            )}
            <Checkbox label={a.showArchivedAdditions} checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            <span className="food-additions-count">{live.length}</span>
          </div>
        </div>

        {usages && (
          <div className="menu-alert" role="status">
            <strong>
              {t.usedBy} {usages.addition ? localized(usages.addition.title, lang) || usages.addition.shortName : ""}
            </strong>
            {usages.list.length === 0 && <p>{a.notUsed}</p>}
            <ul>
              {usages.list.map((usage) => (
                <li key={`${usage.menuId}-${usage.itemId}`}>
                  {usage.menuName} → {localized(usage.itemName, lang)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {loading ? (
          <p className="food-additions-empty">{t.loading}</p>
        ) : additions.length === 0 ? (
          <p className="food-additions-empty">{t.empty}</p>
        ) : (
          additions.map((addition) => (
              <div className="food-addition-group-card" key={addition._id}>
                <div className="food-addition-group-info">
                  <h5>
                    {localized(addition.title, lang) || "-"} <small className="menu-secondary">{addition.shortName}</small>
                    {addition.archivedAt ? (
                      <Tags variant="neutral" size="sm">
                        {a.archivedTag}
                      </Tags>
                    ) : (
                      addition.isActive === false && (
                        <Tags variant="warning" size="sm">
                          {a.inactive}
                        </Tags>
                      )
                    )}
                  </h5>
                  <p>{addition.title?.en || "-"}</p>
                  <div className="food-addition-group-meta">
                    <span>{addition.isMandatory ? t.mandatory : t.optional}</span>
                    <span>
                      {addition.options?.length || 0} {t.options}
                    </span>
                    <span>
                      {addition.min} - {addition.max}
                    </span>
                  </div>
                </div>
                <div className="food-addition-group-actions">
                  <ActionMenu
                    label={`${a.additionActions}: ${addition.shortName}`}
                    items={(() => {
                      const index = live.findIndex((entry) => entry._id === addition._id);
                      const archived = Boolean(addition.archivedAt);
                      return [
                        { key: "edit", label: a.edit, icon: <Pen size={16} />, hidden: !editable || archived, onClick: () => handleEdit(addition) },
                        { key: "active", label: addition.isActive === false ? a.reactivate : a.deactivate, icon: addition.isActive === false ? <Eye size={16} /> : <EyeOff size={16} />, hidden: !editable || archived, onClick: () => run(() => updateMenuAdditionAPI(addition._id, { isActive: addition.isActive === false })) },
                        { key: "up", label: a.moveUp, icon: <ArrowUp size={16} />, hidden: !editable || archived || index <= 0, onClick: () => reorder(addition, -1) },
                        { key: "down", label: a.moveDown, icon: <ArrowDown size={16} />, hidden: !editable || archived || index < 0 || index >= live.length - 1, onClick: () => reorder(addition, 1) },
                        { key: "usages", label: a.whereUsed, icon: <Search size={16} />, divider: true, hidden: archived, onClick: () => showUsages(addition) },
                        { key: "copy", label: a.copyToRestaurant, icon: <Copy size={16} />, hidden: !editable || archived || !role.isLud || restaurants.length < 2, onClick: () => setCopy({ type: "ADDITION-GROUP", source: addition._id }) },
                        { key: "archive", label: a.archive, icon: <Archive size={16} />, tone: "danger", divider: true, hidden: !editable || archived, onClick: () => setConfirmArchive(addition) },
                        { key: "restore", label: a.restore, icon: <ArchiveRestore size={16} />, hidden: !editable || !archived, onClick: () => run(() => restoreMenuAdditionAPI(addition._id), a.restored) },
                      ].map((entry) => ({ ...entry, disabled: busy }));
                    })()}
                  />
                </div>
              </div>
          ))
        )}
      </section>

      {editable && (
        <section className="food-additions-panel">
          <div className="food-form-section-title">
            <h4>{editingId ? t.formEdit : t.formAdd}</h4>
            {editingId && (
              <Button type="button" variant="warning" appearance="soft" size="sm" rounded onClick={resetForm}>
                {t.cancelEdit}
              </Button>
            )}
          </div>

          <Input label={t.shortName} helperText={t.shortNameHint} value={formData.shortName} onChange={(e) => setFormData((prev) => ({ ...prev, shortName: e.target.value }))} />
          <div className="food-two-cols">
            <Input fullWidth={false} label={t.arName} value={formData.title?.ar || ""} onChange={(e) => setFormData((prev) => ({ ...prev, title: { ...prev.title, ar: e.target.value } }))} />
            <Input fullWidth={false} label={t.enName} value={formData.title?.en || ""} onChange={(e) => setFormData((prev) => ({ ...prev, title: { ...prev.title, en: e.target.value } }))} />
          </div>
          <div className="food-three-cols">
            <Input type="number" min="0" label={t.min} value={formData.min} onChange={(e) => setFormData((prev) => ({ ...prev, min: e.target.value }))} fullWidth={false} />
            <Input type="number" min="1" label={t.max} value={formData.max} onChange={(e) => setFormData((prev) => ({ ...prev, max: e.target.value }))} fullWidth={false} />
            <Checkbox label={t.mandatory} checked={formData.isMandatory} onChange={(e) => setFormData((prev) => ({ ...prev, isMandatory: e.target.checked }))} />
          </div>
          <Switch label={a.activeLabel} checked={formData.isActive !== false} onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))} />

          <div className="food-form-section">
            <div className="food-form-section-title">
              <h4>{t.options}</h4>
              <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<Plus size={16} />} aria-label={t.options} onClick={() => setFormData((prev) => ({ ...prev, options: [...prev.options, { name: { ar: "", en: "" }, price: 0 }] }))} />
            </div>
            {formData.options.map((option, index) => (
              <div className="food-form-option" key={index}>
                <span className="food-order-badge">{index + 1}</span>
                <Input label={t.optionAr} value={option.name?.ar || ""} onChange={(e) => updateOption(index, { name: { ...option.name, ar: e.target.value } })} fullWidth={false} />
                <Input label={t.optionEn} value={option.name?.en || ""} onChange={(e) => updateOption(index, { name: { ...option.name, en: e.target.value } })} fullWidth={false} />
                <Input type="number" label={t.price} value={option.price ?? 0} onChange={(e) => updateOption(index, { price: e.target.value })} fullWidth={false} />
                {index > 0 && <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<ArrowUp size={16} />} aria-label={a.moveOptionUp} title={a.moveOptionUp} onClick={() => moveOption(index, index - 1)} />}
                {index < formData.options.length - 1 && <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<ArrowDown size={16} />} aria-label={a.moveOptionDown} title={a.moveOptionDown} onClick={() => moveOption(index, index + 1)} />}
                <Button type="button" variant="danger" appearance="soft" size="sm" rounded icon={<Trash2 size={16} />} aria-label={lang === "ar" ? "حذف الخيار" : "Remove option"} onClick={() => setFormData((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }))} />
              </div>
            ))}
          </div>

          {notice && <p className="menu-note-box">{notice}</p>}
          {error && (
            <p className="menu-error" role="alert">
              {error}
            </p>
          )}
          <div className="food-modal-actions">
            <Button type="button" variant="primary" appearance="filled" disabled={busy} loading={busy} onClick={handleSave}>
              {t.save}
            </Button>
          </div>
        </section>
      )}

      {confirmArchive && (
        <ConfirmModal
          tone="danger"
          title={a.confirmArchiveAddition}
          message={`${localized(confirmArchive.title, lang) || confirmArchive.shortName}\n${a.confirmArchiveAdditionBody}`}
          confirmText={a.archive}
          cancelText={a.cancel}
          onConfirm={async () => {
            const target = confirmArchive;
            setConfirmArchive(null);
            await run(() => archiveMenuAdditionAPI(target._id));
          }}
          onCancel={() => setConfirmArchive(null)}
        />
      )}

      {copyContext && <MenuSelectionModal copyType="ADDITION-GROUP" restaurants={restaurants.filter((r) => String(r._id) !== String(restaurantId))} onSubmit={handleCopySubmit} onClose={() => setCopy(null)} />}
    </div>
  );
}
